import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import FileAttachments from '../components/FileAttachments';
import { AlertOctagon, Plus, XCircle, CheckCircle2, ChevronDown, ChevronUp, Paperclip, Send, Pencil, Trash2, AlertCircle } from 'lucide-react';
import { BadgeEstadoFirma, ModalPasarPdteFirma, ModalRevertirBorrador, esEstadoFirmado, IconoBloqueado } from '../components/FlujoFirmaAcciones';
import { ModalEliminarRegistro, esAdmin } from '../components/RegistroAcciones';

// Catálogo de tipos de incidencia (según hoja oficial)
const TIPOS_INCIDENCIA = [
  { key: 'falta_hoja_transportista', label: 'Falta hoja transportista' },
  { key: 'falta_hoja_albaran', label: 'Falta hoja de albarán' },
  { key: 'danos_bultos', label: 'Daños en los bultos' },
  { key: 'danos_productos', label: 'Daños en los productos' },
  { key: 'error_cantidades', label: 'Error en cantidades' },
  { key: 'otros', label: 'Varios / Otros' }
];

// Parsear tipos_incidencia que llega como JSON string
function parseTipos(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return []; }
}

// ═══════════════════════════════════════════════════════════════
// SELECTOR DE TIPOS DE INCIDENCIA (multi-select con checkboxes)
// ═══════════════════════════════════════════════════════════════
function SelectorTipos({ value, onChange, disabled }) {
  const tipos = Array.isArray(value) ? value : [];
  const toggle = (key) => {
    if (tipos.includes(key)) onChange(tipos.filter(t => t !== key));
    else onChange([...tipos, key]);
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      {TIPOS_INCIDENCIA.map(t => {
        const activo = tipos.includes(t.key);
        return (
          <button type="button" key={t.key} disabled={disabled}
            onClick={() => toggle(t.key)}
            style={{
              padding: '8px 12px', borderRadius: 8, border: '2px solid',
              borderColor: activo ? '#dc2626' : 'var(--color-border)',
              background: activo ? '#fee2e2' : 'white',
              color: activo ? '#991b1b' : 'var(--color-text-2)',
              fontWeight: activo ? 600 : 400,
              cursor: disabled ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              textAlign: 'left', fontSize: '0.85rem'
            }}>
            <span style={{
              width: 16, height: 16, borderRadius: 4, border: '2px solid',
              borderColor: activo ? '#dc2626' : 'var(--color-border)',
              background: activo ? '#dc2626' : 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              {activo && <CheckCircle2 size={10} color="white" />}
            </span>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FORMULARIO compartido (crear + editar)
// ═══════════════════════════════════════════════════════════════
function FormularioIncidencia({ form, set, disabled, proveedores, usuarios }) {
  const tipos = Array.isArray(form.tipos_incidencia) ? form.tipos_incidencia : [];
  const tieneOtros = tipos.includes('otros');

  return (
    <>
      <h4 style={{ marginBottom: 10, color: 'var(--color-primary)' }}>Datos del producto</h4>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Fecha *</label>
          <input type="date" className="form-control" value={form.fecha} onChange={e => set('fecha', e.target.value)} disabled={disabled} />
        </div>
        <div className="form-group">
          <label className="form-label">Producto</label>
          <input className="form-control" value={form.producto} onChange={e => set('producto', e.target.value)} disabled={disabled} />
        </div>
      </div>
      <div className="form-grid-3">
        <div className="form-group">
          <label className="form-label">Lote</label>
          <input className="form-control" value={form.lote} onChange={e => set('lote', e.target.value)} disabled={disabled} />
        </div>
        <div className="form-group">
          <label className="form-label">Cantidad</label>
          <input className="form-control" value={form.cantidad} onChange={e => set('cantidad', e.target.value)} disabled={disabled} />
        </div>
        <div className="form-group">
          <label className="form-label">Caducidad</label>
          <input type="date" className="form-control" value={form.caducidad} onChange={e => set('caducidad', e.target.value)} disabled={disabled} />
        </div>
      </div>

      <h4 style={{ marginTop: 16, marginBottom: 10, color: 'var(--color-primary)' }}>Datos del envío</h4>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Proveedor</label>
          <input list="lista-proveedores-inc" className="form-control" value={form.proveedor} onChange={e => set('proveedor', e.target.value)} disabled={disabled} />
          <datalist id="lista-proveedores-inc">
            {proveedores?.map(p => <option key={p.id} value={p.nombre} />)}
          </datalist>
        </div>
        <div className="form-group">
          <label className="form-label">Nº Albarán</label>
          <input className="form-control" value={form.num_albaran} onChange={e => set('num_albaran', e.target.value)} disabled={disabled} />
        </div>
      </div>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Fecha Albarán</label>
          <input type="date" className="form-control" value={form.fecha_albaran} onChange={e => set('fecha_albaran', e.target.value)} disabled={disabled} />
        </div>
        <div className="form-group">
          <label className="form-label">Recepcionado por</label>
          <input list="lista-usuarios-inc" className="form-control" value={form.recepcionado_por} onChange={e => set('recepcionado_por', e.target.value)} disabled={disabled} />
          <datalist id="lista-usuarios-inc">
            {usuarios?.map(u => <option key={u.id} value={`${u.nombre} ${u.apellidos || ''}`.trim()} />)}
          </datalist>
        </div>
      </div>

      <h4 style={{ marginTop: 16, marginBottom: 10, color: 'var(--color-primary)' }}>Incidencia en (marcar todas las que apliquen)</h4>
      <SelectorTipos value={tipos} onChange={(v) => set('tipos_incidencia', v)} disabled={disabled} />

      {tieneOtros && (
        <div className="form-group" style={{ marginTop: 10 }}>
          <label className="form-label">Especifica "Varios / Otros"</label>
          <input className="form-control" value={form.tipo_otros} onChange={e => set('tipo_otros', e.target.value)} disabled={disabled} />
        </div>
      )}

      <div className="form-group" style={{ marginTop: 16 }}>
        <label className="form-label">Observaciones</label>
        <textarea className="form-control" rows={3} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} disabled={disabled} />
      </div>

      <div className="form-group">
        <label className="form-label">Vº Bº Técnico Responsable</label>
        <input list="lista-usuarios-inc" className="form-control" value={form.vbo_tecnico} onChange={e => set('vbo_tecnico', e.target.value)} disabled={disabled} />
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Crear nueva incidencia
// ═══════════════════════════════════════════════════════════════
function ModalIncidencia({ onClose, onGuardado, proveedores, usuarios }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    fecha: today, producto: '', lote: '', cantidad: '', caducidad: '',
    proveedor: '', num_albaran: '', recepcionado_por: '', fecha_albaran: '',
    tipos_incidencia: [], tipo_otros: '',
    observaciones: '', vbo_tecnico: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedId, setSavedId] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const guardar = async () => {
    if (!form.fecha) { setError('La fecha es obligatoria'); return; }
    if (!form.tipos_incidencia || form.tipos_incidencia.length === 0) {
      setError('Selecciona al menos un tipo de incidencia'); return;
    }
    setLoading(true);
    try { const r = await api.post('/registros/incidencias', form); setSavedId(r.data.id); }
    catch (e) { setError(e.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertOctagon size={18} color="var(--color-primary)" />Nueva Hoja de Incidencias
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger">{error}</div>}
          {savedId && <div className="alert alert-success"><CheckCircle2 size={16} /> Incidencia guardada en estado Borrador. Puedes adjuntar fotos del daño.</div>}

          <FormularioIncidencia form={form} set={set} disabled={!!savedId} proveedores={proveedores} usuarios={usuarios} />

          <FileAttachments seccion="incidencias" entidadTipo="incidencia" entidadId={savedId} label="Adjuntar fotografías o documentación de la incidencia" />
        </div>
        <div className="modal-footer">
          {!savedId ? (
            <><button className="btn btn-outline" onClick={onClose}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardar} disabled={loading}>
                {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />} Guardar
              </button></>
          ) : (
            <><button className="btn btn-outline" onClick={onClose}>Cerrar sin más archivos</button>
              <button className="btn btn-primary" onClick={onGuardado}><CheckCircle2 size={16} /> Finalizar</button></>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Editar incidencia (admin + contraseña)
// ═══════════════════════════════════════════════════════════════
function ModalEditarIncidencia({ registro, onClose, onActualizado, proveedores, usuarios }) {
  const [form, setForm] = useState({
    fecha: registro.fecha, producto: registro.producto || '',
    lote: registro.lote || '', cantidad: registro.cantidad || '', caducidad: registro.caducidad || '',
    proveedor: registro.proveedor || '', num_albaran: registro.num_albaran || '',
    recepcionado_por: registro.recepcionado_por || '', fecha_albaran: registro.fecha_albaran || '',
    tipos_incidencia: parseTipos(registro.tipos_incidencia), tipo_otros: registro.tipo_otros || '',
    observaciones: registro.observaciones || '', vbo_tecnico: registro.vbo_tecnico || ''
  });
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleGuardar = async () => {
    if (!password) { setError('Debes introducir tu contraseña'); return; }
    setLoading(true); setError('');
    try {
      await api.put(`/registros/incidencias/${registro.id}`, { ...form, password });
      onActualizado();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pencil size={20} color="#3b82f6" /> Editar Incidencia {registro.numero_incidencia}
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning" style={{ fontSize: '0.85rem' }}>
            <AlertCircle size={16} /> <strong>Acción restringida:</strong> Solo administradores. Se requiere contraseña.
          </div>

          <FormularioIncidencia form={form} set={set} disabled={false} proveedores={proveedores} usuarios={usuarios} />

          <div className="form-group" style={{ marginTop: 16, padding: 12, background: '#fef3c7', borderRadius: 8, borderLeft: '4px solid #f59e0b' }}>
            <label className="form-label" style={{ color: '#92400e', fontWeight: 600 }}>🔐 Confirma con tu contraseña</label>
            <input type="password" className="form-control" value={password} onChange={e => setPassword(e.target.value)} placeholder="Tu contraseña actual" />
          </div>

          {error && <div className="alert alert-danger">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleGuardar} disabled={loading || !password}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />}
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FILA con todos los botones de acción
// ═══════════════════════════════════════════════════════════════
function FilaIncidencia({ r, isAdmin, onPasarPdte, onRevertir, onEditar, onEliminar }) {
  const [expanded, setExpanded] = useState(false);
  const estado = r.estado || 'borrador';
  const bloqueado = esEstadoFirmado(estado, false);
  const tipos = parseTipos(r.tipos_incidencia);
  const tiposLabels = tipos.map(t => TIPOS_INCIDENCIA.find(x => x.key === t)?.label || t).join(', ');

  return (
    <>
      <tr>
        <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: '#dc2626', fontWeight: 700 }}>{r.numero_incidencia}</span></td>
        <td>{new Date(r.fecha).toLocaleDateString('es-ES')}</td>
        <td style={{ fontWeight: 500 }}>{r.producto || '—'}</td>
        <td style={{ fontSize: '0.82rem' }}>{r.proveedor || '—'}</td>
        <td style={{ fontSize: '0.8rem', color: 'var(--color-text-2)', maxWidth: 240 }}>{tiposLabels || '—'}</td>
        <td><BadgeEstadoFirma estado={estado} /></td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FileAttachments seccion="incidencias" entidadTipo="incidencia" entidadId={r.id} compact />
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {estado === 'borrador' && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onPasarPdte(r)}
                title="Pasar a Pdte. firma" style={{ color: '#f59e0b' }}>
                <Send size={13} />
              </button>
            )}

            {estado === 'pendiente_firma' && isAdmin && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onRevertir(r)}
                title="Revertir a Borrador (admin)" style={{ color: '#6b7280' }}>
                <ChevronUp size={13} style={{ transform: 'rotate(-90deg)' }} />
              </button>
            )}

            {isAdmin && !bloqueado && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEditar(r)}
                title="Editar (admin)" style={{ color: '#3b82f6' }}>
                <Pencil size={13} />
              </button>
            )}

            {isAdmin && !bloqueado && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEliminar(r)}
                title="Eliminar (admin)" style={{ color: 'var(--color-danger)' }}>
                <Trash2 size={13} />
              </button>
            )}

            {bloqueado && <IconoBloqueado estado={estado} />}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr><td colSpan={7} style={{ padding: '0 14px 14px', background: 'var(--color-gray-1)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, padding: '12px 0' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-2)', marginBottom: 2 }}>Lote / Cantidad / Caducidad</div>
              <div style={{ fontWeight: 500 }}>{r.lote || '—'} · {r.cantidad || '—'} · {r.caducidad ? new Date(r.caducidad).toLocaleDateString('es-ES') : '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-2)', marginBottom: 2 }}>Albarán</div>
              <div style={{ fontWeight: 500 }}>{r.num_albaran || '—'} ({r.fecha_albaran ? new Date(r.fecha_albaran).toLocaleDateString('es-ES') : 'sin fecha'})</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-2)', marginBottom: 2 }}>Recepcionado por</div>
              <div>{r.recepcionado_por || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-2)', marginBottom: 2 }}>Vº Bº Técnico</div>
              <div>{r.vbo_tecnico || '—'}</div>
            </div>
            {r.tipo_otros && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-2)', marginBottom: 2 }}>Otros tipos</div>
                <div>{r.tipo_otros}</div>
              </div>
            )}
            {r.observaciones && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-2)', marginBottom: 2 }}>Observaciones</div>
                <div style={{ fontSize: '0.85rem' }}>{r.observaciones}</div>
              </div>
            )}
          </div>
          <FileAttachments seccion="incidencias" entidadTipo="incidencia" entidadId={r.id} label="Subir fotografías o documentación" />
        </td></tr>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// PÁGINA principal
// ═══════════════════════════════════════════════════════════════
export default function IncidenciasPage() {
  const [registros, setRegistros] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editarRegistro, setEditarRegistro] = useState(null);
  const [eliminarRegistro, setEliminarRegistro] = useState(null);
  const [pasarPdteRegistro, setPasarPdteRegistro] = useState(null);
  const [revertirRegistro, setRevertirRegistro] = useState(null);
  const [success, setSuccess] = useState('');
  const isAdmin = esAdmin();

  const load = () => { setLoading(true); api.get('/registros/incidencias').then(r => setRegistros(r.data)).finally(() => setLoading(false)); };
  const loadProveedores = () => { api.get('/proveedores').then(r => setProveedores(r.data)).catch(() => {}); };
  const loadUsuarios = () => { api.get('/usuarios').then(r => setUsuarios(r.data)).catch(() => {}); };

  useEffect(() => { load(); loadProveedores(); loadUsuarios(); }, []);

  const mostrarSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlertOctagon size={24} color="var(--color-primary)" />Hoja de Incidencias
          </h1>
          <p>{registros.length} incidencias · Cada incidencia se firma individualmente por la Directora Técnica</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={16} /> Nueva Incidencia</button>
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}><CheckCircle2 size={16} /> {success}</div>}

      <div className="card">
        {loading ? <div className="loading-overlay"><div className="spinner" /></div> :
          registros.length === 0 ? <div className="empty-state"><AlertOctagon size={48} /><h3>Sin incidencias registradas</h3></div> : (
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th>Nº</th><th>Fecha</th><th>Producto</th><th>Proveedor</th>
                  <th>Tipos de incidencia</th><th>Estado</th><th><Paperclip size={13} /></th>
                </tr></thead>
                <tbody>{registros.map(r =>
                  <FilaIncidencia key={r.id} r={r} isAdmin={isAdmin}
                    onPasarPdte={setPasarPdteRegistro}
                    onRevertir={setRevertirRegistro}
                    onEditar={setEditarRegistro}
                    onEliminar={setEliminarRegistro} />)}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {modal && <ModalIncidencia onClose={() => setModal(false)}
        onGuardado={() => { setModal(false); load(); }}
        proveedores={proveedores} usuarios={usuarios} />}

      {pasarPdteRegistro && <ModalPasarPdteFirma
        endpoint={`/registros/incidencias/${pasarPdteRegistro.id}/pasar-pdte-firma`}
        descripcion={`${pasarPdteRegistro.numero_incidencia} · ${pasarPdteRegistro.producto || 'incidencia'}`}
        onClose={() => setPasarPdteRegistro(null)}
        onConfirmado={() => { setPasarPdteRegistro(null); load(); mostrarSuccess('Marcado como pendiente de firma. Ve a Firmas Pendientes.'); }} />}

      {revertirRegistro && <ModalRevertirBorrador
        endpoint={`/registros/incidencias/${revertirRegistro.id}/reset-estado`}
        descripcion={`${revertirRegistro.numero_incidencia} · ${revertirRegistro.producto || 'incidencia'}`}
        onClose={() => setRevertirRegistro(null)}
        onConfirmado={() => { setRevertirRegistro(null); load(); mostrarSuccess('Revertido a borrador'); }} />}

      {editarRegistro && <ModalEditarIncidencia registro={editarRegistro}
        proveedores={proveedores} usuarios={usuarios}
        onClose={() => setEditarRegistro(null)}
        onActualizado={() => { setEditarRegistro(null); load(); mostrarSuccess('Incidencia actualizada'); }} />}

      {eliminarRegistro && <ModalEliminarRegistro
        endpoint={`/registros/incidencias/${eliminarRegistro.id}`}
        descripcion={`${eliminarRegistro.numero_incidencia} · ${eliminarRegistro.producto || 'incidencia'}`}
        onClose={() => setEliminarRegistro(null)}
        onEliminado={() => { setEliminarRegistro(null); load(); mostrarSuccess('Incidencia eliminada'); }} />}
    </div>
  );
}
